import { Component, Input, forwardRef, ViewChild, ElementRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, Calendar } from 'lucide-angular';

@Component({
    selector: 'app-date-picker',
    standalone: true,
    imports: [CommonModule, FormsModule, LucideAngularModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatePickerComponent),
            multi: true,
        },
    ],
    templateUrl: './date-picker.component.html',
    styleUrl: './date-picker.component.scss',
})
export class DatePickerComponent implements ControlValueAccessor {
    @Input() format: 'MM/dd/yyyy' | 'dd/MM/yyyy' | 'yyyy-MM-dd' = 'MM/dd/yyyy';
    @Input() inputClass: string = 'input input--primary w-full pr-10';
    @Input() placeholder?: string;
    @Input() iconClass: string = 'w-5 h-5 text-primary';
    @Input() type: 'date' | 'datetime-local' = 'date';

    @ViewChild('hiddenInput', { static: false }) hiddenInput!: ElementRef<HTMLInputElement>;

    readonly Calendar = Calendar;

    displayValue: string = '';
    internalValue: string = '';
    disabled: boolean = false;

    get effectivePlaceholder(): string {
        return this.placeholder || this.format.toLowerCase();
    }

    private onChange: (value: string) => void = () => {};
    onTouched: () => void = () => {};

    writeValue(value: string): void {
        if (value) {
            // Detect if the value is in native format (yyyy-MM-dd) or display format
            if (this.isNativeFormat(value)) {
                // Value is in native format, convert to display format
                this.internalValue = value.includes('T') ? value : value;
                this.displayValue = this.convertToDisplayFormat(value);
            } else {
                // Value is already in display format
                this.internalValue = this.convertToNativeFormat(value);
                this.displayValue = value;
            }
        } else {
            this.internalValue = '';
            this.displayValue = '';
        }
    }

    private isNativeFormat(value: string): boolean {
        // Native format is yyyy-MM-dd or yyyy-MM-ddTHH:mm
        const datePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?$/;
        return datePattern.test(value);
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onInputChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const nativeValue = input.value; // yyyy-MM-dd or yyyy-MM-ddTHH:mm from native picker

        if (nativeValue) {
            // Convert from native format to display format
            const displayValue = this.convertToDisplayFormat(nativeValue);
            this.displayValue = displayValue;
            this.onChange(displayValue);
        } else {
            this.displayValue = '';
            this.onChange('');
        }

        this.internalValue = nativeValue;
    }

    openPicker(): void {
        if (!this.disabled && this.hiddenInput) {
            // Trigger the native picker
            this.hiddenInput.nativeElement.focus();
            this.hiddenInput.nativeElement.click();

            // For browsers that support showPicker()
            if ('showPicker' in HTMLInputElement.prototype) {
                try {
                    (this.hiddenInput.nativeElement as any).showPicker();
                } catch (e) {
                    // showPicker not supported or failed, fallback to click already done
                }
            }
        }
    }

    private convertToNativeFormat(displayValue: string): string {
        if (!displayValue) return '';

        let day: string,
            month: string,
            year: string,
            time: string = '';

        // Check if it includes time (for datetime-local)
        const hasTime = displayValue.includes(' ') && displayValue.split(' ').length === 2;
        if (hasTime && this.type === 'datetime-local') {
            const [datePart, timePart] = displayValue.split(' ');
            time = timePart;
            displayValue = datePart;
        }

        switch (this.format) {
            case 'MM/dd/yyyy':
                [month, day, year] = displayValue.split('/');
                break;
            case 'dd/MM/yyyy':
                [day, month, year] = displayValue.split('/');
                break;
            case 'yyyy-MM-dd':
                return hasTime ? `${displayValue}T${time}` : displayValue; // Already in native format
            default:
                return hasTime ? `${displayValue}T${time}` : displayValue;
        }

        const dateStr = `${year}-${month}-${day}`;
        return hasTime ? `${dateStr}T${time}` : dateStr;
    }

    private convertToDisplayFormat(nativeValue: string): string {
        if (!nativeValue) return '';

        // Handle datetime-local format (yyyy-MM-ddTHH:mm)
        if (this.type === 'datetime-local' && nativeValue.includes('T')) {
            const [datePart, timePart] = nativeValue.split('T');
            const [year, month, day] = datePart.split('-');

            switch (this.format) {
                case 'MM/dd/yyyy':
                    return `${month}/${day}/${year} ${timePart}`;
                case 'dd/MM/yyyy':
                    return `${day}/${month}/${year} ${timePart}`;
                case 'yyyy-MM-dd':
                    return nativeValue.replace('T', ' ');
                default:
                    return nativeValue.replace('T', ' ');
            }
        }

        // Handle date format (yyyy-MM-dd)
        const [year, month, day] = nativeValue.split('-');

        switch (this.format) {
            case 'MM/dd/yyyy':
                return `${month}/${day}/${year}`;
            case 'dd/MM/yyyy':
                return `${day}/${month}/${year}`;
            case 'yyyy-MM-dd':
                return nativeValue;
            default:
                return nativeValue;
        }
    }
}
